/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { FC, useState } from 'react'
import { Form } from 'react-final-form'
import { IconButton } from '@mui/material'
import { MenuSelectField } from 'components/Select/MenuSelectField'
import { GroupIconsAWS } from 'constants/consts'
import { findAllStrategyGroup_findAllStrategyGroup_items as Group } from '__generatedTypes__/findAllStrategyGroup'
import { GroupIcon } from '__generatedTypes__/globalTypes'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import client from 'configs/apolloClient'
import { InputField } from 'components/Input'
import { validateRequired } from 'utils/validation'
import { FieldValues } from 'utils/types'
import {
  CREATE_STRATEGY_GROUP,
  FIND_ALL_STRATEGY_GROUP,
  REMOVE_STRATEGY_GROUP,
  UPDATE_STRATEGY_GROUP,
} from 'queries/strategies'
import { createStrategyGroup, createStrategyGroupVariables } from '__generatedTypes__/createStrategyGroup'
import { removeStrategyGroup, removeStrategyGroupVariables } from '__generatedTypes__/removeStrategyGroup'
import { updateStrategyGroup, updateStrategyGroupVariables } from '__generatedTypes__/updateStrategyGroup'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import * as classes from 'pagesNew/FlashCards/FlashCardsGroups/styles'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import Alert from 'components/Alert'
import { toast } from 'utils/toast'

interface Props {
  group: Group | null
  availableGroupColors: string[]
  handleClose: () => void
  groupsLength: number
  selectedSubjectId: string
}

export const ManageStrategiesGroupsModal: FC<Props> = ({
  group,
  groupsLength,
  selectedSubjectId,
  availableGroupColors,
  handleClose,
}) => {
  const colors = group ? [...availableGroupColors, group.color] : availableGroupColors
  const [updateStrategyGroup, { loading: updateLoading }] = useMutation<
    updateStrategyGroup,
    updateStrategyGroupVariables
  >(UPDATE_STRATEGY_GROUP)
  const [alertMessage, setAlertMessage] = useState('')

  const [removeStrategyGroup] = useMutation<removeStrategyGroup, removeStrategyGroupVariables>(REMOVE_STRATEGY_GROUP)

  const handleCloseAndUpdate = () => {
    handleClose()
    client.refetchQueries({
      include: [FIND_ALL_STRATEGY_GROUP],
    })
  }

  const [createStrategyGroup, { loading: createLoading }] = useMutation<
    createStrategyGroup,
    createStrategyGroupVariables
  >(CREATE_STRATEGY_GROUP)

  const handleGroupRemove = async () => {
    if (group?.id) {
      //TODO Error handling
      try {
        await removeStrategyGroup({ variables: { removeStrategyGroupId: group.id } })
        handleCloseAndUpdate()

        toast.success('Group deleted successfully')
      } catch (error) {
        if (error instanceof Error) {
          setAlertMessage(error.message)
        }
      }
    }
  }

  const handleSubmit = async (data: FieldValues) => {
    if (group) {
      try {
        await updateStrategyGroup({
          variables: {
            updateStrategyGroupInputFieldName: {
              id: data.id,
              color: data.color,
              title: data.title,
              icon: data.icon,
              description: data.description ?? '',
            },
          },
        })
        handleCloseAndUpdate()

        toast.success('Group updated successfully')
      } catch (error) {
        if (error instanceof Error) {
          setAlertMessage(error.message)
        }
      }
    } else {
      try {
        await createStrategyGroup({
          variables: {
            createStrategyGroupInput: {
              color: data.color,
              title: data.title,
              description: data.description ?? '',
              icon: data.icon,
              order: groupsLength,
              subject: {
                id: selectedSubjectId,
              },
            },
          },
        })
        handleCloseAndUpdate()

        toast.success('Group created successfully')
      } catch (error) {
        if (error instanceof Error) {
          setAlertMessage(error.message)
        }
      }
    }
  }

  return (
    <div css={commonClasses.modalContainer}>
      <Form
        onSubmit={handleSubmit}
        initialValues={group}
        render={({ handleSubmit, values }) => (
          <form css={commonClasses.modalForm} onSubmit={handleSubmit}>
            <Typography css={commonClasses.modalTitle} variant="title2">
              {group ? 'Edit Group' : 'Create Group'}
            </Typography>
            <InputField name="title" validate={validateRequired} label="Group Title" fullWidth />
            <div css={classes.titleColor}>
              <Typography variant="body2" css={commonClasses.label}>
                Color
              </Typography>
              <Typography variant="body2" css={commonClasses.label}>
                Category
              </Typography>
              <MenuSelectField css={commonClasses.select} fullWidth name="color" required>
                {colors.map((color) => (
                  <MenuItem value={color} key={color} css={classes.menuItem}>
                    <div css={classes.menuItemColor(color)} />
                  </MenuItem>
                ))}
              </MenuSelectField>
              <MenuSelectField css={commonClasses.select} fullWidth name="icon" required>
                {Object.values(GroupIcon).map((icon: GroupIcon) => (
                  <MenuItem value={icon} key={icon} css={classes.menuItem}>
                    <img src={GroupIconsAWS[icon]} alt="Group Icons" css={classes.icon} />
                  </MenuItem>
                ))}
              </MenuSelectField>
            </div>
            <Typography variant="body2" css={commonClasses.label}>
              Description
            </Typography>
            <TextareaAutosize value={values.description} css={commonClasses.descriptionInput} name="description" />
            {alertMessage && (
              <Alert
                severity="error"
                message={alertMessage}
                icon={false}
                action={
                  <IconButton color="inherit" size="small" onClick={() => setAlertMessage('')}>
                    <CrossIcon fontSize="inherit" />
                  </IconButton>
                }
              />
            )}
            <div css={commonClasses.buttons}>
              {!!group && (
                <Button
                  css={commonClasses.button}
                  variant="outlined"
                  color="red"
                  size="large"
                  onClick={handleGroupRemove}
                >
                  <TrashIcon />
                  Delete
                </Button>
              )}
              <Button css={commonClasses.button} variant="outlined" color="blue" size="large" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                css={[commonClasses.button, !!group && commonClasses.fullButton]}
                variant="contained"
                type="submit"
                color="blue"
                size="large"
              >
                {createLoading || updateLoading ? '...Loading' : 'Submit'}
              </Button>
            </div>
          </form>
        )}
      />
    </div>
  )
}
